/**
 * +mage-specs: bot kits for the Mage specialisations (docs/design/classes/specs.md "Mage", the
 * rotation sections). The APL presses evolved ids directly (the spec'd ClassDef binds them). Auto,
 * Group Finder strangers and the harness all play these.
 */
import { CINDERBORN, CINDERBORN_AURA, RIMECALLER, RIMECALLER_AURA, STORMCALLER, STORMCALLER_AURA } from '@keystone/content';
import type { ClassKit } from '../kits';

const SC = STORMCALLER;
const SCA = STORMCALLER_AURA;
const RC = RIMECALLER;
const RCA = RIMECALLER_AURA;
const CB = CINDERBORN;
const CBA = CINDERBORN_AURA;

/** A fight worth a cooldown: a sturdy target or a pack. */
const bigFight = { type: 'any' as const, of: [{ type: 'targetHpAbove' as const, pct: 0.5 }, { type: 'targets' as const, atLeast: 3, radius: 10 }] };

/**
 * Stormcaller: Spark to 100, then ride Overvolt to ~120 and vent with Skybreak (sooner when hurt or
 * on the move); Chain Storm and the Wandering Tempest on cooldown; Stormheart for a free long ride.
 */
export const stormcallerKit: ClassKit = {
  classId: 'mage',
  role: 'dps',
  ranged: true,
  personalDefensive: SC.arcWard,
  blink: SC.staticStep,
  apl: [
    { ability: SC.stormheart, when: [{ type: 'inCombat' }, bigFight] },
    { ability: SC.arcWard, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.6 }] },
    { ability: SC.staticCage, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 7 }] },
    // Vent: near the cap, when the Overvolt burn starts to hurt, or a pack is lined up for the chain.
    { ability: SC.skybreak, when: [{ type: 'resource', resource: 'voltage', atLeast: 118 }] },
    { ability: SC.skybreak, when: [{ type: 'resource', resource: 'voltage', atLeast: 101 }, { type: 'selfHpBelow', pct: 0.5 }, { type: 'selfLacksAura', auraId: SCA.stormheart }] },
    { ability: SC.skybreak, when: [{ type: 'resource', resource: 'voltage', atLeast: 100 }, { type: 'targets', atLeast: 3, radius: 12 }] },
    { ability: SC.wanderingTempest, target: 'enemy', when: [{ type: 'targetDistBelow', yd: 26 }] },
    { ability: SC.chainStorm, target: 'groundTarget', when: [{ type: 'inCombat' }] },
    { ability: SC.spark, when: [{ type: 'any', of: [{ type: 'not', cond: { type: 'moving' } }, { type: 'selfAura', auraId: SCA.staticRush }, { type: 'selfAura', auraId: SCA.stormheart }] }] },
    { ability: SC.skybreak, when: [{ type: 'moving' }, { type: 'resource', resource: 'voltage', atLeast: 60 }] },
    { ability: SC.chainStorm, target: 'groundTarget', when: [{ type: 'moving' }] },
  ],
};

/**
 * Rimecaller: Rime Tithe banks Spines; Hoarlance at 5 (Brimming: a guaranteed fracture), or at 4+
 * into a freeze / Hoarbite / Deep Winter. Whiteout and the Wandering Glacier on cooldown; Rime Ring
 * when a pack reaches you.
 */
export const rimecallerKit: ClassKit = {
  classId: 'mage',
  role: 'dps',
  ranged: true,
  personalDefensive: RC.frostMantle,
  blink: RC.frostfold,
  apl: [
    { ability: RC.deepWinter, target: 'enemy', when: [{ type: 'inCombat' }, bigFight] },
    { ability: RC.frostMantle, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.75 }] },
    { ability: RC.rimeRing, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 7 }] },
    { ability: RC.hoarlance, when: [{ type: 'selfAura', auraId: RCA.brimming }] },
    { ability: RC.hoarlance, when: [{ type: 'selfAura', auraId: RCA.deepWinter }, { type: 'resource', resource: 'spines', atLeast: 3 }] },
    {
      ability: RC.hoarlance,
      when: [{ type: 'resource', resource: 'spines', atLeast: 4 }, { type: 'any', of: [{ type: 'selfAura', auraId: RCA.hoarbite }, { type: 'targetAura', auraId: RCA.frozen }] }],
    },
    // Finish a dying target with what is banked (solo: the next pull is already coming).
    { ability: RC.hoarlance, when: [{ type: 'targetHpBelow', pct: 0.25 }, { type: 'resource', resource: 'spines', atLeast: 2 }] },
    { ability: RC.wanderingGlacier, target: 'enemy', when: [{ type: 'targetDistBelow', yd: 26 }] },
    { ability: RC.whiteout, target: 'groundTarget', when: [{ type: 'inCombat' }] },
    { ability: RC.rimeTithe, when: [{ type: 'not', cond: { type: 'moving' } }] },
    { ability: RC.hoarlance, when: [{ type: 'moving' }, { type: 'resource', resource: 'spines', atLeast: 2 }] },
    { ability: RC.whiteout, target: 'groundTarget', when: [{ type: 'moving' }] },
  ],
};

/**
 * Cinderborn: Ember Lash builds Cinders, Cinderfall spends 3 (the big crater above 60% health);
 * Pyre Burst and the Wandering Sun on cooldown; Phoenix Dive and Ashrise Pact keep you alive; never
 * spend health you can't afford when a pack is on you.
 */
export const cinderbornKit: ClassKit = {
  classId: 'mage',
  role: 'dps',
  ranged: true,
  personalDefensive: CB.ashrisePact,
  blink: CB.phoenixDive,
  apl: [
    { ability: CB.ashrisePact, when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'selfHpBelow', pct: 0.35 }, { type: 'all', of: [{ type: 'selfAura', auraId: CBA.ashesCooling }, { type: 'selfHpBelow', pct: 0.7 }] }] }] },
    { ability: CB.kindledHeart, when: [{ type: 'inCombat' }, bigFight] },
    { ability: CB.infernoRing, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 7 }] },
    { ability: CB.phoenixDive, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 6 }, { type: 'selfHpBelow', pct: 0.75 }] },
    { ability: CB.cinderfall, when: [{ type: 'resource', resource: 'cinders', atLeast: 3 }] },
    { ability: CB.wanderingSun, target: 'enemy', when: [{ type: 'targetDistBelow', yd: 26 }] },
    { ability: CB.pyreBurst, target: 'groundTarget', when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'not', cond: { type: 'selfHpBelow', pct: 0.3 } }, { type: 'selfAura', auraId: CBA.phoenix }, { type: 'selfAura', auraId: CBA.kindledHeart }] }] },
    { ability: CB.emberLash, when: [{ type: 'any', of: [{ type: 'not', cond: { type: 'moving' } }, { type: 'selfAura', auraId: CBA.flashfire }] }] },
    { ability: CB.pyreBurst, target: 'groundTarget', when: [{ type: 'moving' }, { type: 'inCombat' }] },
  ],
};

/** Spec id → kit. */
export const MAGE_SPEC_KITS: Readonly<Record<string, ClassKit>> = { stormcaller: stormcallerKit, rimecaller: rimecallerKit, cinderborn: cinderbornKit };
